import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE, apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn, EmptyState, Spinner, Money, StatusBadge } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import DocumentEditor from "@/app/components/DocumentEditor";
import { FileText, Plus, MoreVertical, Download, Send, CheckCircle2, ArrowRightLeft } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function Quotes() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const { data, loading, refetch } = useFetch(`/quotes?status=${status}`, [status]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (params.get("new")) { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, []); // eslint-disable-line

  const create = async (payload) => {
    setSaving(true);
    try { await api.post("/quotes", payload); toast.success("Quote created"); setOpen(false); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const setStatusFn = async (id, s) => { await api.post(`/quotes/${id}/status`, { status: s }); toast.success(`Marked ${s}`); refetch(); };
  const convert = async (id) => { await api.post(`/quotes/${id}/convert`); toast.success("Converted to invoice"); refetch(); };
  const viewPdf = (id) => window.open(`${API_BASE}/quotes/${id}/pdf`, "_blank");

  const items = data?.items || [];
  const TABS = ["", "draft", "sent", "accepted", "declined"];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Quotes & Estimates" description="Create branded quotes and track their status."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-quote-button"><Plus size={16} /> New quote</Btn>} />
      <div className="flex gap-1 mb-4 overflow-x-auto">
        {TABS.map((t) => <button key={t} onClick={() => setStatus(t)} data-testid={`quote-tab-${t || "all"}`}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${status === t ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t ? t[0].toUpperCase() + t.slice(1) : "All"}</button>)}
      </div>

      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="No quotes yet" description="Create a quote and send a professional PDF to your customer."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> New quote</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Number</th><th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Valid until</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th><th className="px-4 py-3 font-semibold">Status</th><th></th></tr></thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`quote-row-${q.number}`}>
                  <td className="px-4 py-3 font-mono font-medium text-navy">{q.number}</td>
                  <td className="px-4 py-3 text-slate-600">{q.customer_name}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{fmtDate(q.valid_until)}</td>
                  <td className="px-4 py-3 text-right"><Money value={q.total} symbol={sym} /></td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button data-testid={`quote-menu-${q.number}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><MoreVertical size={16} /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => viewPdf(q.id)}><Download size={15} className="mr-2" /> View PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatusFn(q.id, "sent")}><Send size={15} className="mr-2" /> Mark sent</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatusFn(q.id, "accepted")}><CheckCircle2 size={15} className="mr-2" /> Mark accepted</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => convert(q.id)}><ArrowRightLeft size={15} className="mr-2" /> Convert to invoice</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto amden-scroll">
          <DialogHeader><DialogTitle>New quote</DialogTitle></DialogHeader>
          <DocumentEditor kind="quote" onSave={create} saving={saving} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
